import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, Select, Tag, Table, Typography, Checkbox, Alert, App as AntApp, Flex,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../../config';
import type { Permissao } from './types';
import { GRUPOS_COLUNAS, RISCO_TAG_COLOR, MODULO_LABEL } from './types';
import { getToken } from './utils';

const { Title, Text } = Typography;

// Matriz de permissões — quem pode fazer cada operação.
export default function AdminPermissoes() {
  const { message } = AntApp.useApp();
  const [permissoes, setPermissoes] = useState<Permissao[]>([]);
  const [loading, setLoading] = useState(true);
  const [salvandoId, setSalvandoId] = useState<string | null>(null);
  const [erro, setErro] = useState('');
  const [filtroModulo, setFiltroModulo] = useState<string>('');
  const [filtroRisco, setFiltroRisco] = useState<string>('');

  async function carregar() {
    setLoading(true); setErro('');
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_listar_permissoes' }, { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setPermissoes(Array.isArray(d) ? d : []);
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }
  useEffect(() => { carregar(); /* eslint-disable-line */ }, []);

  async function toggleGrupo(p: Permissao, grupoId: string) {
    if (grupoId === 'admin') {
      message.warning('admin sempre tem acesso (safeguard).');
      return;
    }
    const novoGrupos = p.grupos.includes(grupoId)
      ? p.grupos.filter(g => g !== grupoId) : [...p.grupos, grupoId];
    setSalvandoId(p.operacao_id);
    setPermissoes(prev => prev.map(x => x.operacao_id === p.operacao_id ? { ...x, grupos: novoGrupos } : x));
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_atualizar_permissao', operacao_id: p.operacao_id, grupos: novoGrupos },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setPermissoes(prev => prev.map(x => x.operacao_id === p.operacao_id
        ? { ...x, grupos: d.grupos, atualizado_em: d.atualizado_em, atualizado_por: d.atualizado_por } : x));
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
      carregar();
    } finally { setSalvandoId(null); }
  }

  const permsFiltradas = permissoes.filter(p =>
    (!filtroModulo || p.modulo === filtroModulo) &&
    (!filtroRisco || p.risco === filtroRisco));

  const cols: ColumnsType<Permissao> = [
    {
      title: 'Operação', dataIndex: 'descricao', key: 'desc',
      render: (_, p) => (
        <div>
          <Text strong style={{ fontSize: 12 }}>{p.descricao}</Text>
          <div><Text type="secondary" style={{ fontSize: 9, fontFamily: 'monospace' }}>{p.operacao_id}</Text></div>
        </div>
      ),
    },
    {
      title: 'Risco', dataIndex: 'risco', key: 'r', width: 90, align: 'center',
      render: (v) => <Tag color={RISCO_TAG_COLOR[v]}>{v}</Tag>,
    },
    {
      title: 'Módulo', dataIndex: 'modulo', key: 'm', width: 110,
      render: (v) => <Text style={{ fontSize: 11 }}>{MODULO_LABEL[v] || v}</Text>,
    },
    ...GRUPOS_COLUNAS.map(g => ({
      title: <Text style={{ fontSize: 9, color: g.cor, textTransform: 'uppercase', letterSpacing: 0.3 }}>{g.label}</Text>,
      key: g.id, width: 100, align: 'center' as const,
      render: (_: any, p: Permissao) => {
        const isAdmin = g.id === 'admin';
        return (
          <Checkbox checked={p.grupos.includes(g.id) || isAdmin}
            disabled={isAdmin || salvandoId === p.operacao_id}
            onChange={() => toggleGrupo(p, g.id)}
            title={isAdmin ? 'admin sempre tem acesso' : ''} />
        );
      },
    })),
  ];

  return (
    <div>
      <Title level={5} style={{ margin: '0 0 4px', color: '#020D7F' }}>
        🔐 Matriz de permissões — quem pode fazer cada operação
      </Title>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 14 }}>
        Marque grupos Cognito que podem executar cada operação. <strong>admin sempre tem acesso</strong>.
        Mudanças entram em vigor em até 60s (cache).
      </Text>

      {erro && <Alert type="error" message={erro} showIcon style={{ marginBottom: 12 }} />}

      <Card size="small" style={{ marginBottom: 12 }} bodyStyle={{ padding: 10 }}>
        <Flex gap={10} align="center">
          <Text strong style={{ fontSize: 11 }}>Filtros:</Text>
          <Select value={filtroModulo || undefined} onChange={(v) => setFiltroModulo(v || '')}
            placeholder="Todos módulos" allowClear size="small" style={{ width: 160 }}
            options={Object.entries(MODULO_LABEL).map(([id, label]) => ({ label, value: id }))} />
          <Select value={filtroRisco || undefined} onChange={(v) => setFiltroRisco(v || '')}
            placeholder="Todos riscos" allowClear size="small" style={{ width: 130 }}
            options={Object.keys(RISCO_TAG_COLOR).map(r => ({ label: r, value: r }))} />
          <Text type="secondary" style={{ fontSize: 11, marginLeft: 'auto' }}>
            {permsFiltradas.length} de {permissoes.length} operações
          </Text>
        </Flex>
      </Card>

      <Table rowKey="operacao_id" columns={cols} dataSource={permsFiltradas} loading={loading}
        size="small" pagination={{ pageSize: 50, showSizeChanger: true }} scroll={{ x: 'max-content' }} />

      <Alert type="info" showIcon style={{ marginTop: 12 }}
        message={<Text style={{ fontSize: 11 }}>💡 Mudanças levam até <strong>60s</strong> pra entrar em vigor (cache). HTTP 403 com mensagem clara se faltar permissão.</Text>} />
    </div>
  );
}
